import { useState } from 'react';
import { apiFetch } from '../api';
import { todayCDT, nowCDT } from '../dateUtils';

function GoalsSection({ goalsData, onUpdate, onManage }) {
  const [pendingToggles, setPendingToggles] = useState({});
  const today = todayCDT();
  const weekStart = getWeekStart();

  const todayGoals = goalsData.goals.filter(
    (g) => g.frequency === 'daily' || g.frequency === 'weekly'
  );

  function getWeekStart() {
    const d = nowCDT();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
  }

  function getDateKey(goal) {
    return goal.frequency === 'daily' ? today : weekStart;
  }

  function isCompleted(goal) {
    const dateKey = getDateKey(goal);
    const toggleKey = `${goal.id}_${dateKey}`;

    // Check optimistic state first
    if (toggleKey in pendingToggles) {
      return pendingToggles[toggleKey];
    }

    return goalsData.completions.some(
      (c) => c.goalId === goal.id && c.date === dateKey
    );
  }

  async function toggleGoal(goal) {
    const dateKey = getDateKey(goal);
    const toggleKey = `${goal.id}_${dateKey}`;
    const wasCompleted = isCompleted(goal);

    // Optimistic update — toggle immediately
    setPendingToggles((prev) => ({ ...prev, [toggleKey]: !wasCompleted }));

    try {
      await apiFetch('/api/goals/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goalId: goal.id, date: dateKey }),
      });
      // Fetch updated data, then clear optimistic state
      await onUpdate();
      setPendingToggles((prev) => {
        const next = { ...prev };
        delete next[toggleKey];
        return next;
      });
    } catch (err) {
      // Revert on failure
      setPendingToggles((prev) => {
        const next = { ...prev };
        delete next[toggleKey];
        return next;
      });
      console.error('Failed to toggle goal:', err);
    }
  }

  const timGoals = todayGoals.filter((g) => g.person === 'Tim');
  const emilioGoals = todayGoals.filter((g) => g.person === 'Emilio');

  return (
    <section className="card goals-section">
      <div className="goals-header">
        <h2>Today's Goals</h2>
        <button className="manage-btn" onClick={onManage}>
          Manage Goals
        </button>
      </div>
      <div className="goals-people-row">
        <PersonGoals
          name="Tim"
          goals={timGoals}
          isCompleted={isCompleted}
          onToggle={toggleGoal}
        />
        <PersonGoals
          name="Emilio"
          goals={emilioGoals}
          isCompleted={isCompleted}
          onToggle={toggleGoal}
        />
      </div>
      {todayGoals.length === 0 && (
        <p className="empty">No goals set. Tap "Manage Goals" to add some!</p>
      )}
    </section>
  );
}

function PersonGoals({ name, goals, isCompleted, onToggle }) {
  if (goals.length === 0) return null;

  return (
    <div className="person-goals">
      <h3>{name}</h3>
      <ul className="goal-list">
        {goals.map((goal) => {
          const completed = isCompleted(goal);
          return (
            <li key={goal.id} className={`goal-item ${completed ? 'completed' : ''}`}>
              <button
                className="goal-check"
                onClick={() => onToggle(goal)}
                aria-label={completed ? 'Mark incomplete' : 'Mark complete'}
              >
                {completed ? '\u2705' : '\u2B1C'}
              </button>
              <span className="goal-title">{goal.title}</span>
              <span className="goal-freq">{goal.frequency}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default GoalsSection;
